import { AmendmentField, parseAmendmentFieldId } from './amendment-field.model';
import { diffAmendmentFieldIds } from './amendment-fields.diff';
import { extractAmendmentFields } from './amendment-fields.extractor';

/**
 * Works out which fields the user changed while a form is open.
 *
 * EMR widgets keep loading patient data into the form after it opens, so comparing with the form
 * as it was loaded would report fields nobody typed in. Instead a widget is remembered as it
 * stood when the user first interacted with it, and only a remembered widget can report changes.
 */
export class AmendmentChangeTracker {
  private readonly baselines = new Map<string, AmendmentField[]>();
  private allCaptured = false;

  /**
   * Forgets every remembered widget; call when another form is loaded.
   */
  public reset(): void {
    this.baselines.clear();
    this.allCaptured = false;
  }

  /**
   * Remembers how a widget looks right now, before the user changes it. Without `widgetId` every
   * widget not remembered yet is captured: an interaction outside the widgets, such as applying
   * Scribe suggestions, may change any of them.
   */
  public capture(formData: unknown, widgetId?: string): void {
    if (this.allCaptured || (widgetId && this.baselines.has(widgetId))) {
      return;
    }

    const fieldsByWidget = groupByWidget(extractAmendmentFields(formData));

    if (widgetId) {
      this.baselines.set(widgetId, fieldsByWidget.get(widgetId) ?? []);
      return;
    }

    fieldsByWidget.forEach((fields, id) => {
      if (!this.baselines.has(id)) {
        this.baselines.set(id, fields);
      }
    });
    this.allCaptured = true;
  }

  /**
   * Ids of the fields that differ from how their widget looked when it was remembered. A widget
   * that was never remembered reports nothing.
   */
  public changedFieldIds(formData: unknown): string[] {
    const current = extractAmendmentFields(formData);
    // Once everything is captured, a widget without a baseline had no fields back then.
    const baseline = this.allCaptured
      ? []
      : current.filter((field) => !this.baselines.has(widgetIdOf(field)));

    this.baselines.forEach((fields) => baseline.push(...fields));

    return diffAmendmentFieldIds(baseline, current);
  }
}

function widgetIdOf(field: AmendmentField): string {
  return parseAmendmentFieldId(field.id)?.widgetId ?? field.id;
}

function groupByWidget(fields: AmendmentField[]): Map<string, AmendmentField[]> {
  const groups = new Map<string, AmendmentField[]>();

  for (const field of fields) {
    const widgetId = widgetIdOf(field);
    const group = groups.get(widgetId) ?? [];
    group.push(field);
    groups.set(widgetId, group);
  }

  return groups;
}
